/*
 * Hirsch Hill revamp: behaviour layer.
 * Runs alongside theme.js. Never replaces its cart/search/variant logic; only adds presentation behaviour.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduceMotion = root.classList.contains('hh-reduced-motion');

  /* ------------------------------------------------------------------ *
   * Age gate
   * ------------------------------------------------------------------ */
  function initAgeGate() {
    var gate = document.getElementById('hh-age');
    if (!gate) return;

    if (!root.classList.contains('hh-age-pending')) {
      gate.remove();
      return;
    }

    gate.hidden = false;
    requestAnimationFrame(function () { gate.classList.add('is-open'); });

    var accept = gate.querySelector('[data-hh-age="accept"]');
    if (accept) accept.focus({ preventScroll: true });

    gate.addEventListener('click', function (event) {
      var action = event.target.closest('[data-hh-age]');
      if (!action) return;

      if (action.getAttribute('data-hh-age') === 'deny') {
        window.location.href = 'https://www.google.com';
        return;
      }

      try { localStorage.setItem('ageVerified', 'true'); } catch (e) {}
      gate.classList.remove('is-open');
      gate.classList.add('is-leaving');
      root.classList.remove('hh-age-pending');
      setTimeout(function () { gate.remove(); }, 900);
    });

    // Keep focus inside the gate
    gate.addEventListener('keydown', function (event) {
      if (event.key !== 'Tab') return;
      var focusables = gate.querySelectorAll('button');
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) { last.focus(); event.preventDefault(); }
      else if (!event.shiftKey && document.activeElement === last) { first.focus(); event.preventDefault(); }
    });
  }

  /* ------------------------------------------------------------------ *
   * Header: overlay mode, scrolled / hidden states
   * ------------------------------------------------------------------ */
  var header;
  var headerSection;

  // Fixed panels (mobile menu, mobile search) hang off the header's real bottom edge,
  // which includes the announcement bar while the page is at the top.
  function setHeaderBottom() {
    if (!header) return;
    // Measure where the header sits when shown: ignore the slide-up transform it has while
    // hidden on scroll, otherwise a panel opened right after scrolling covers the header.
    var bottom = header.getBoundingClientRect().bottom;
    if (headerSection) {
      var t = getComputedStyle(headerSection).transform;
      if (t && t !== 'none') bottom -= new DOMMatrixReadOnly(t).m42;
    }
    root.style.setProperty('--hh-header-bottom', Math.max(0, Math.round(bottom)) + 'px');
  }

  function measureHeader() {
    if (!headerSection) return;
    setHeaderBottom();
    root.style.setProperty('--hh-header-h', headerSection.offsetHeight + 'px');
    var announcement = document.querySelector('.shopify-section--announcement-bar');
    root.style.setProperty('--hh-announcement-h', (announcement ? announcement.offsetHeight : 0) + 'px');
  }

  function initHeader() {
    header = document.querySelector('[data-hh-header]');
    if (!header) return;
    headerSection = header.closest('.shopify-section') || header.parentNode;

    // Overlay header when the first section on the page is a full-bleed hero
    var firstSection = document.querySelector('#main > .shopify-section:first-child');
    var overlay = firstSection && firstSection.querySelector('[data-hh-overlay-header]');
    root.classList.toggle('hh-header-overlay', !!overlay);

    measureHeader();
    window.addEventListener('resize', measureHeader, { passive: true });

    var lastY = window.scrollY;
    var ticking = false;

    function update() {
      var y = window.scrollY;
      var threshold = overlay ? Math.max(40, (overlay.offsetHeight || 400) * 0.12) : 8;
      root.classList.toggle('hh-scrolled', y > threshold);

      var menuOpen = root.classList.contains('hh-lock');
      var goingDown = y > lastY + 4;
      var goingUp = y < lastY - 4;
      if (!menuOpen && y > 320 && goingDown) root.classList.add('hh-header-hidden');
      if (goingUp || y <= 320 || menuOpen) root.classList.remove('hh-header-hidden');

      lastY = y;
      ticking = false;
      setHeaderBottom();
    }

    window.addEventListener('scroll', function () {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ------------------------------------------------------------------ *
   * Desktop dropdowns and mega menu
   * ------------------------------------------------------------------ */
  function initDropdowns() {
    var items = document.querySelectorAll('[data-hh-dropdown]');
    if (!items.length) return;

    var hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    function close(item) {
      if (!item.classList.contains('is-open')) return;
      item.classList.remove('is-open');
      var button = item.querySelector(':scope > button');
      if (button) button.setAttribute('aria-expanded', 'false');
      syncScrim();
    }

    function closeAll(except) {
      items.forEach(function (item) { if (item !== except) close(item); });
    }

    function open(item) {
      closeAll(item);
      item.classList.add('is-open');
      var button = item.querySelector(':scope > button');
      if (button) button.setAttribute('aria-expanded', 'true');
      syncScrim();
    }

    items.forEach(function (item) {
      var button = item.querySelector(':scope > button');
      var timer;

      if (button) {
        button.addEventListener('click', function () {
          item.classList.contains('is-open') ? close(item) : open(item);
        });
      }

      if (hoverCapable) {
        item.addEventListener('mouseenter', function () {
          clearTimeout(timer);
          timer = setTimeout(function () { open(item); }, 90);
        });
        item.addEventListener('mouseleave', function () {
          clearTimeout(timer);
          timer = setTimeout(function () { close(item); }, 180);
        });
      }

      item.addEventListener('focusout', function (event) {
        if (!item.contains(event.relatedTarget)) close(item);
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var openItem = document.querySelector('[data-hh-dropdown].is-open');
      if (openItem) {
        close(openItem);
        var button = openItem.querySelector(':scope > button');
        if (button) button.focus();
      }
    });

    document.addEventListener('click', function (event) {
      if (!event.target.closest('[data-hh-dropdown]')) closeAll();
    });
  }

  /* ------------------------------------------------------------------ *
   * Scrim + scroll lock, driven by the state of theme.js components
   * ------------------------------------------------------------------ */
  function syncScrim() {
    setHeaderBottom();
    var miniCart = document.getElementById('mini-cart');
    var mobileMenu = document.getElementById('mobile-menu');
    var search = document.querySelector('.hh-search');

    var cartOpen = miniCart && miniCart.getAttribute('aria-hidden') === 'false';
    var menuOpen = mobileMenu && mobileMenu.getAttribute('aria-hidden') === 'false';
    var searchOpen = search && search.classList.contains('is-visible');
    var megaOpen = !!document.querySelector('.hh-nav__item--mega.is-open');

    root.classList.toggle('hh-cart-open', !!cartOpen);
    root.classList.toggle('hh-menu-open', !!menuOpen);
    root.classList.toggle('hh-search-open', !!searchOpen);
    root.classList.toggle('hh-mega-open', megaOpen);
    root.classList.toggle('hh-lock', !!(cartOpen || menuOpen || searchOpen));
    root.classList.toggle('hh-scrim-on', !!(cartOpen || searchOpen || megaOpen));
    // Re-measure once the header has settled into its shown position
    requestAnimationFrame(setHeaderBottom);
  }

  function initStateObservers() {
    var targets = [document.getElementById('mini-cart'), document.getElementById('mobile-menu'), document.querySelector('.hh-search')];
    var observer = new MutationObserver(function () {
      syncScrim();
      var search = document.querySelector('.hh-search');
      if (search && search.classList.contains('is-visible') && !search.dataset.hhFocused) {
        search.dataset.hhFocused = '1';
        var input = search.querySelector('.search-bar__input');
        if (input) setTimeout(function () { input.focus({ preventScroll: true }); }, 60);
      }
      if (search && !search.classList.contains('is-visible')) delete search.dataset.hhFocused;
    });

    targets.forEach(function (target) {
      if (target) observer.observe(target, { attributes: true, attributeFilter: ['aria-hidden', 'class'] });
    });

    // The mini-cart is re-rendered by theme.js; keep the observer attached to the live node.
    document.addEventListener('cart:rerendered', syncScrim, true);

    var scrim = document.querySelector('.hh-scrim');
    if (scrim) {
      scrim.addEventListener('click', function () {
        var search = document.querySelector('.hh-search.is-visible');
        if (search) {
          var toggle = document.querySelector('.hh-header [data-action="toggle-search"]');
          if (toggle) toggle.click();
        }
        document.querySelectorAll('[data-hh-dropdown].is-open').forEach(function (item) {
          item.classList.remove('is-open');
        });
        syncScrim();
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      var search = document.querySelector('.hh-search.is-visible');
      if (search) {
        var toggle = document.querySelector('.hh-header [data-action="toggle-search"]');
        if (toggle) toggle.click();
      }
    });

    syncScrim();
  }

  /* ------------------------------------------------------------------ *
   * Cart count bubble: hide when empty
   * ------------------------------------------------------------------ */
  function initCartCount() {
    var count = document.querySelector('.hh-cart-count');
    if (!count) return;
    var sync = function () { count.setAttribute('data-count', (count.textContent || '0').trim()); };
    new MutationObserver(sync).observe(count, { childList: true, characterData: true, subtree: true });
    sync();
  }

  /* ------------------------------------------------------------------ *
   * Cart page summary: sticky, but never taller than the window allows.
   * When the panel is taller than the space under the header, stick it by
   * its bottom edge instead so the checkout button and payment icons stay in view.
   * ------------------------------------------------------------------ */
  function initCartRecap() {
    if (!document.querySelector('.cart-recap')) return;
    var desktop = window.matchMedia('(min-width: 1000px)');
    var frame = 0;
    var watched = null;
    var observer = new ResizeObserver(function () { schedule(); });

    var update = function () {
      frame = 0;
      var recap = document.querySelector('.cart-recap');
      if (!recap) return;
      if (recap !== watched) { observer.observe(recap); watched = recap; }
      if (!desktop.matches) { recap.style.removeProperty('--hh-recap-top'); return; }
      var header = parseFloat(getComputedStyle(root).getPropertyValue('--header-height')) || 80;
      var top = header + 30;
      var fit = window.innerHeight - recap.offsetHeight - 24;
      if (fit < top) recap.style.setProperty('--hh-recap-top', fit + 'px');
      else recap.style.removeProperty('--hh-recap-top');
    };
    var schedule = function () { if (!frame) frame = requestAnimationFrame(update); };

    // The panel grows when the note opens; the page grows or the section re-renders when lines change
    observer.observe(document.body);
    window.addEventListener('resize', schedule);
    update();
  }

  /* ------------------------------------------------------------------ *
   * Scroll reveal
   * ------------------------------------------------------------------ */
  var revealObserver;

  function initReveal(scope) {
    var elements = (scope || document).querySelectorAll('[data-hh-reveal]:not(.is-in)');
    if (!elements.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      elements.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    }

    elements.forEach(function (el) {
      // Stagger children marked with data-hh-stagger
      if (el.hasAttribute('data-hh-stagger')) {
        Array.prototype.forEach.call(el.children, function (child, index) {
          child.style.setProperty('--hh-i', index);
        });
      }
      revealObserver.observe(el);
    });
  }

  /* ------------------------------------------------------------------ *
   * Parallax (transform only, active only while in view)
   * ------------------------------------------------------------------ */
  function initParallax(scope) {
    if (reduceMotion) return;
    var elements = (scope || document).querySelectorAll('[data-hh-parallax]');
    if (!elements.length) return;

    var active = new Set();
    var running = false;

    function frame() {
      var vh = window.innerHeight;
      active.forEach(function (el) {
        var rect = el.parentElement.getBoundingClientRect();
        var speed = parseFloat(el.getAttribute('data-hh-parallax')) || 0.12;
        var progress = (rect.top + rect.height / 2 - vh / 2) / (vh + rect.height);
        el.style.transform = 'translate3d(0,' + (progress * speed * -100).toFixed(2) + '%,0) scale(' + (1 + speed) + ')';
      });
      if (active.size) requestAnimationFrame(frame); else running = false;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) active.add(entry.target); else active.delete(entry.target);
      });
      if (active.size && !running) { running = true; requestAnimationFrame(frame); }
    });

    elements.forEach(function (el) { observer.observe(el); });
  }

  /* ------------------------------------------------------------------ *
   * Number count-up
   * ------------------------------------------------------------------ */
  function initCounters(scope) {
    var counters = (scope || document).querySelectorAll('[data-hh-count]');
    if (!counters.length) return;

    var run = function (el) {
      var target = parseFloat(el.getAttribute('data-hh-count'));
      if (isNaN(target)) return;
      if (reduceMotion) { el.textContent = target; return; }
      var duration = 1800;
      var start = performance.now();
      var from = target > 1900 ? target - 40 : 0;
      (function tick(now) {
        var t = Math.max(0, Math.min(1, (now - start) / duration));
        var eased = 1 - Math.pow(1 - t, 4);
        el.textContent = Math.round(from + (target - from) * eased);
        if (t < 1) requestAnimationFrame(tick);
      })(start);
    };

    if (!('IntersectionObserver' in window)) { counters.forEach(run); return; }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { run(entry.target); observer.unobserve(entry.target); }
      });
    }, { threshold: 0.6 });
    counters.forEach(function (el) { observer.observe(el); });
  }

  /* ------------------------------------------------------------------ *
   * Horizontal rails (product showcase): buttons, drag, progress
   * ------------------------------------------------------------------ */
  function initRails(scope) {
    (scope || document).querySelectorAll('[data-hh-rail]').forEach(function (wrapper) {
      if (wrapper.dataset.hhRailReady) return;
      wrapper.dataset.hhRailReady = '1';

      var track = wrapper.querySelector('[data-hh-rail-track]');
      var prev = wrapper.querySelector('[data-hh-rail-prev]');
      var next = wrapper.querySelector('[data-hh-rail-next]');
      var bar = wrapper.querySelector('[data-hh-rail-progress]');
      if (!track) return;

      var step = function () {
        var item = track.firstElementChild;
        return item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : track.clientWidth * 0.8;
      };

      var update = function () {
        var max = track.scrollWidth - track.clientWidth;
        var ratio = max > 0 ? track.scrollLeft / max : 0;
        if (bar) bar.style.transform = 'scaleX(' + Math.max(0.08, Math.min(1, (track.clientWidth / track.scrollWidth) + ratio * (1 - track.clientWidth / track.scrollWidth))) + ')';
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max - 2;
        wrapper.classList.toggle('is-static', max <= 2);
      };

      if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { track.scrollBy({ left: step(), behavior: 'smooth' }); });
      track.addEventListener('scroll', function () { requestAnimationFrame(update); }, { passive: true });
      window.addEventListener('resize', update, { passive: true });

      // Mouse drag to scroll (touch already scrolls natively)
      var dragging = false, startX = 0, startLeft = 0, moved = false;
      // is-dragging (which disables pointer events on the cards) is only set once the mouse
      // actually moves, so a plain click still reaches the card's link.
      track.addEventListener('pointerdown', function (event) {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        dragging = true; moved = false;
        startX = event.clientX; startLeft = track.scrollLeft;
      });
      window.addEventListener('pointermove', function (event) {
        if (!dragging) return;
        var dx = event.clientX - startX;
        if (!moved && Math.abs(dx) > 4) { moved = true; track.classList.add('is-dragging'); }
        if (moved) track.scrollLeft = startLeft - dx;
      });
      // Stop the browser's native link/image drag from hijacking the gesture
      track.addEventListener('dragstart', function (event) { event.preventDefault(); });
      window.addEventListener('pointerup', function () {
        if (!dragging) return;
        dragging = false;
        track.classList.remove('is-dragging');
      });
      track.addEventListener('click', function (event) {
        if (moved) { event.preventDefault(); event.stopPropagation(); moved = false; }
      }, true);

      update();
    });
  }

  /* ------------------------------------------------------------------ *
   * Video toggles (hero)
   * ------------------------------------------------------------------ */
  function initVideos(scope) {
    (scope || document).querySelectorAll('[data-hh-video-toggle]').forEach(function (button) {
      if (button.dataset.hhReady) return;
      button.dataset.hhReady = '1';
      var video = button.closest('[data-hh-video-scope]').querySelector('video');
      if (!video) { button.hidden = true; return; }

      if (reduceMotion) { video.pause(); video.removeAttribute('autoplay'); }

      var sync = function () {
        button.classList.toggle('is-paused', video.paused);
        button.setAttribute('aria-label', video.paused ? 'Play background video' : 'Pause background video');
      };
      button.addEventListener('click', function () { video.paused ? video.play() : video.pause(); });
      video.addEventListener('play', sync);
      video.addEventListener('pause', sync);
      sync();
    });
  }

  /* ------------------------------------------------------------------ *
   * Split headings into lines/words for the mask reveal
   * ------------------------------------------------------------------ */
  function initSplit(scope) {
    (scope || document).querySelectorAll('[data-hh-split]:not([data-hh-split-done])').forEach(function (el) {
      el.setAttribute('data-hh-split-done', '');
      // Words only animate inside a revealed ancestor; give orphan headings their own trigger
      if (!el.closest('[data-hh-reveal]') && !el.closest('.hh-hero')) el.setAttribute('data-hh-reveal', '');
      var index = 0;
      var walk = function (node) {
        Array.prototype.slice.call(node.childNodes).forEach(function (child) {
          if (child.nodeType === 3) {
            var fragment = document.createDocumentFragment();
            var text = child.textContent;
            // Punctuation straight after an element (e.g. "<em>hill</em>,") is glued to that
            // element so the line can't break before it and leave the comma orphaned.
            var prev = child.previousSibling;
            var lead = prev && prev.nodeType === 1 ? text.match(/^[^\s\w]+/) : null;
            if (lead) {
              var punct = document.createElement('span');
              punct.className = 'hh-word-punct';
              punct.textContent = lead[0];
              var glue = document.createElement('span');
              glue.className = 'hh-nowrap';
              prev.parentNode.insertBefore(glue, prev);
              glue.appendChild(prev);
              glue.appendChild(punct);
              text = text.slice(lead[0].length);
            }
            text.split(/(\s+)/).forEach(function (part) {
              if (!part) return;
              if (/^\s+$/.test(part)) { fragment.appendChild(document.createTextNode(part)); return; }
              var outer = document.createElement('span');
              outer.className = 'hh-word';
              var inner = document.createElement('span');
              inner.className = 'hh-word__inner';
              inner.style.setProperty('--hh-w', index++);
              inner.textContent = part;
              outer.appendChild(inner);
              fragment.appendChild(outer);
            });
            child.parentNode.replaceChild(fragment, child);
          } else if (child.nodeType === 1 && child.tagName !== 'BR') {
            walk(child);
          }
        });
      };
      walk(el);
    });
  }

  /* ------------------------------------------------------------------ *
   * Member group tracking
   * A member collection page (data-hh-member-group) remembers its group for 30 days.
   * The group is saved as the cart attribute "Member group" and as the hidden line item
   * property "_member_group" on every item added, so orders can be told apart.
   * ------------------------------------------------------------------ */
  var MEMBER_KEY = 'hh_member_group';
  var MEMBER_DAYS = 30;

  function readMemberGroup() {
    try {
      var saved = JSON.parse(localStorage.getItem(MEMBER_KEY) || 'null');
      if (saved && saved.group && Date.now() - saved.t < MEMBER_DAYS * 864e5) return saved.group;
    } catch (e) {}
    return '';
  }

  function syncMemberAttribute(group, force) {
    try { if (!force && sessionStorage.getItem(MEMBER_KEY) === group) return; } catch (e) {}
    var xhr = new XMLHttpRequest();
    xhr.open('POST', ((window.routes && window.routes.cartUrl) || '/cart') + '/update.js');
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.onload = function () {
      if (xhr.status < 300) { try { sessionStorage.setItem(MEMBER_KEY, group); } catch (e) {} }
    };
    xhr.send(JSON.stringify({ attributes: { 'Member group': group } }));
  }

  function tagCartForm(form) {
    var group = readMemberGroup();
    if (!form || !group) return;
    var input = form.querySelector('input[name="properties[_member_group]"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_member_group]';
      form.appendChild(input);
    }
    input.value = group;
    // Re-save on add too, in case the cart was emptied or replaced since the last visit
    setTimeout(function () { syncMemberAttribute(group, true); }, 800);
  }

  function initMemberTracking() {
    var page = document.querySelector('[data-hh-member-group]');
    if (page) {
      try { localStorage.setItem(MEMBER_KEY, JSON.stringify({ group: page.getAttribute('data-hh-member-group'), t: Date.now() })); } catch (e) {}
      // Remember the member page itself, for the back link on private product pages
      var crumbTitle = (document.querySelector('.hh-msale .hh-eyebrow, .hh-chero__title') || {}).textContent || '';
      try { localStorage.setItem('hh_member_page', JSON.stringify({ url: location.pathname.split('/products/')[0], title: document.title.split(/\s[–|-]\s/)[0].trim() || crumbTitle.trim() })); } catch (e) {}
    }
    var group = readMemberGroup();
    if (group) syncMemberAttribute(group);

    // Capture phase, so the property is in the form before theme.js serialises it
    ['click', 'submit'].forEach(function (type) {
      document.addEventListener(type, function (event) {
        var target = event.target.closest ? event.target : event.target.parentElement;
        if (!target) return;
        if (type === 'click' && !target.closest('buy-button, [type="submit"], button[name="add"]')) return;
        tagCartForm(target.closest('form[action*="/cart/add"]'));
      }, true);
    });
  }

  /* ------------------------------------------------------------------ *
   * Member pages (body.hh-member-page): keep shoppers on their member range.
   * Links to the homepage or All products (logos, empty cart button) go to the member collection instead.
   * ------------------------------------------------------------------ */
  function memberHome() {
    if (document.querySelector('[data-hh-member-group]')) return location.pathname.split('/products/')[0];
    try {
      var last = JSON.parse(localStorage.getItem('hh_member_page') || 'null');
      if (last && last.url) return last.url;
    } catch (e) {}
    var back = document.querySelector('[data-hh-member-back]');
    return back ? back.getAttribute('href') : '';
  }

  function initMemberLinks() {
    if (!document.body.classList.contains('hh-member-page')) return;
    // Product counts read as deals: "23 deals", "Showing 1 - 23 of 23 deals" (again after sorting re-renders them)
    var dealCounts = function () {
      document.querySelectorAll('.collection__products-count-total, .collection__showing-count').forEach(function (el) {
        if (/\bproducts?\b/i.test(el.textContent)) el.textContent = el.textContent.replace(/\bproducts\b/i, 'deals').replace(/\bproduct\b/i, 'deal');
      });
    };
    var home = memberHome();
    var exits = /^(https?:\/\/[^/]+)?\/(collections\/all\/?)?(\?.*)?$/;
    var retarget = function (scope) {
      dealCounts();
      if (!home) return;
      (scope || document).querySelectorAll('a[href]').forEach(function (a) {
        var href = a.getAttribute('href');
        if (!exits.test(href)) return;
        if (/^https?:/.test(href) && href.indexOf(location.host) === -1 && !/hirschhill\.com|myshopify\.com/.test(href)) return;
        a.setAttribute('href', home);
      });
    };
    retarget(document);
    // The cart drawer re-renders after changes: retarget its links again
    if ('MutationObserver' in window) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        setTimeout(function () { pending = false; retarget(document); }, 60);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  /* ------------------------------------------------------------------ *
   * Member carts: a few cases short of free shipping at checkout.
   * Checkout from the drawer or cart page first offers wines from the member's collection that would take the
   * cart over the threshold, as many cases of one wine as it takes (up to the theme setting, 4 by default),
   * lowest spend first. Adding goes straight on to checkout; "No thanks" checks out as is.
   * Asked once per session.
   * ------------------------------------------------------------------ */
  var NUDGE_KEY = 'hh_ship_nudge_seen';

  function nudgeMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    var amount = (cents / 100).toFixed(2).replace(/\.00$/, '').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, amount);
  }

  function nudgeEscape(text) {
    var div = document.createElement('div');
    div.textContent = text == null ? '' : String(text);
    return div.innerHTML;
  }

  function memberCollectionUrl() {
    try {
      var last = JSON.parse(localStorage.getItem('hh_member_page') || 'null');
      if (last && last.url && /^\/collections\/[^/]+$/.test(last.url)) return last.url;
    } catch (e) {}
    return '';
  }

  function findNudgeOptions(cart, threshold) {
    var hasMember = cart.items.some(function (item) { return item.product_type === 'Private'; });
    var gap = threshold - cart.total_price;
    var url = memberCollectionUrl();
    var maxCases = (window.theme && window.theme.memberFreeShippingCases) || 4;
    if (!hasMember || gap <= 0 || !url) return Promise.resolve(null);
    var inCart = {};
    cart.items.forEach(function (item) { inCart[item.product_id] = true; });
    return fetch(url + '/products.json?limit=250', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.ok ? response.json() : { products: [] }; })
      .then(function (data) {
        var options = [];
        (data.products || []).forEach(function (product) {
          if (inCart[product.id] || product.product_type !== 'Private') return;
          var variant = (product.variants || []).filter(function (v) { return v.available; })[0];
          if (!variant) return;
          var price = Math.round(parseFloat(variant.price) * 100);
          if (!price) return;
          var qty = Math.ceil(gap / price);
          if (qty > maxCases) return;
          var compare = variant.compare_at_price ? Math.round(parseFloat(variant.compare_at_price) * 100) : 0;
          options.push({ id: variant.id, title: product.title, qty: qty, price: price, total: price * qty, compare: compare > price ? compare : 0, image: product.images && product.images[0] ? product.images[0].src : '' });
        });
        // the lowest spend that gets there first, then fewer cases, then the bigger saving
        options.sort(function (a, b) {
          return (a.total - b.total) || (a.qty - b.qty) || ((b.compare - b.price) - (a.compare - a.price));
        });
        return options.length ? { gap: gap, options: options.slice(0, 3) } : null;
      })
      .catch(function () { return null; });
  }

  function nudgeProperties(cart) {
    var properties = {};
    cart.items.some(function (item) {
      if (item.properties && item.properties['Date of Birth']) { properties['Date of Birth'] = item.properties['Date of Birth']; return true; }
      return false;
    });
    var group = readMemberGroup();
    if (group) properties._member_group = group;
    return properties;
  }

  function showShipNudge(cart, found, proceed) {
    var dialog = document.createElement('div');
    dialog.className = 'hh-nudge';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'hh-nudge-title');
    var first = found.options[0];
    var rows = found.options.map(function (option, index) {
      var image = option.image ? option.image.replace(/(\.[a-z]+)(\?|$)/i, '_200x$1$2') : '';
      return '<li class="hh-nudge__item' + (index === 0 ? ' is-best' : '') + '">' +
        (image ? '<img class="hh-nudge__img" src="' + nudgeEscape(image) + '" alt="" loading="lazy">' : '<span class="hh-nudge__img"></span>') +
        '<span class="hh-nudge__info">' +
          (index === 0 ? '<span class="hh-nudge__tag">Best fit</span>' : '') +
          '<span class="hh-nudge__name">' + nudgeEscape(option.title) + '</span>' +
          '<span class="hh-nudge__price">' + (option.qty > 1 ? option.qty + ' &times; ' : '') + nudgeMoney(option.price) + (option.compare ? ' <s>' + nudgeMoney(option.compare) + '</s>' : '') + '</span>' +
        '</span>' +
        '<button type="button" class="hh-nudge__add" data-variant="' + option.id + '" data-qty="' + option.qty + '">' + (option.qty > 1 ? 'Add ' + option.qty + ' &amp; check out' : 'Add &amp; check out') + '</button>' +
      '</li>';
    }).join('');
    dialog.innerHTML =
      '<div class="hh-nudge__backdrop" data-nudge-skip></div>' +
      '<div class="hh-nudge__panel">' +
        '<button type="button" class="hh-nudge__close" data-nudge-skip aria-label="Close">&times;</button>' +
        '<p class="hh-nudge__eyebrow">Free shipping is ' + (first.qty > 1 ? first.qty + ' cases' : 'one case') + ' away</p>' +
        '<h2 class="hh-nudge__title" id="hh-nudge-title">Add ' + (first.qty > 1 ? first.qty + ' cases' : 'a case') + ' of ' + nudgeEscape(first.title.replace(/\s*\(.*\)\s*$/, '')) + ' and your whole order ships free</h2>' +
        '<p class="hh-nudge__text">You&rsquo;re ' + nudgeMoney(found.gap) + ' short of free shipping on orders over ' + nudgeMoney(window.theme.memberFreeShipping) + '. Any one of these gets you there:</p>' +
        '<ul class="hh-nudge__list" role="list">' + rows + '</ul>' +
        '<button type="button" class="hh-nudge__skip">No thanks, check out with shipping</button>' +
      '</div>';
    document.body.appendChild(dialog);
    document.documentElement.classList.add('hh-nudge-open');
    requestAnimationFrame(function () { dialog.classList.add('is-open'); });
    var firstButton = dialog.querySelector('.hh-nudge__add');
    if (firstButton) firstButton.focus();

    function close() {
      document.documentElement.classList.remove('hh-nudge-open');
      dialog.remove();
      document.removeEventListener('keydown', onKey);
    }
    function onKey(event) { if (event.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);

    dialog.addEventListener('click', function (event) {
      var add = event.target.closest('.hh-nudge__add');
      if (add) {
        if (add.classList.contains('is-failed')) { close(); proceed(); return; }
        dialog.querySelectorAll('.hh-nudge__add').forEach(function (button) { button.disabled = true; });
        add.textContent = 'Adding…';
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(add.getAttribute('data-variant')), quantity: Number(add.getAttribute('data-qty')) || 1, properties: nudgeProperties(cart) }] })
        }).then(function (response) {
          if (!response.ok) throw new Error('add failed');
          window.location.href = '/checkout';
        }).catch(function () {
          add.textContent = 'Couldn’t add it, check out instead';
          add.classList.add('is-failed');
          add.disabled = false;
        });
        return;
      }
      if (event.target.closest('.hh-nudge__skip')) { close(); proceed(); return; }
      if (event.target.closest('[data-nudge-skip]')) close();
    });
  }

  function initShipNudge() {
    var threshold = (window.theme && window.theme.memberFreeShipping) || 0;
    if (!threshold) return;
    var bypass = false;
    document.addEventListener('click', function (event) {
      var button = event.target.closest && event.target.closest('button[name="checkout"], input[name="checkout"]');
      if (!button || bypass) return;
      try { if (sessionStorage.getItem(NUDGE_KEY)) return; } catch (e) {}
      event.preventDefault();
      event.stopPropagation();
      var proceed = function () { bypass = true; button.click(); };
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) { return response.json(); })
        .then(function (cart) { return findNudgeOptions(cart, threshold).then(function (found) { return { cart: cart, found: found }; }); })
        .then(function (result) {
          if (!result.found) { proceed(); return; }
          try { sessionStorage.setItem(NUDGE_KEY, '1'); } catch (e) {}
          showShipNudge(result.cart, result.found, proceed);
        })
        .catch(proceed);
    }, true);
  }

  /* ------------------------------------------------------------------ *
   * Product card bottle alignment (collection grids and the homepage carousels)
   * Finds where the bottle sits in each card photo (canvas, same origin) and stores its top, bottom and centre,
   * so the CSS can size and place every bottle to the same height and base line. Wide shots (cases, gift packs)
   * are left as they are.
   * ------------------------------------------------------------------ */
  function measureBottle(img) {
    if (img.dataset.hhMeasured) return;
    img.dataset.hhMeasured = '1';
    try {
      var w = 120, h = Math.round(w * img.naturalHeight / img.naturalWidth);
      if (!h) return;
      var canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      var ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, w, h);
      var data = ctx.getImageData(0, 0, w, h).data;
      var top = -1, bottom = -1, left = w, right = -1;
      for (var y = 0; y < h; y++) {
        var count = 0;
        for (var x = 0; x < w; x++) {
          var i = (y * w + x) * 4;
          if (data[i + 3] > 40 && (data[i] < 215 || data[i + 1] < 215 || data[i + 2] < 215)) {
            count++;
            if (x < left) left = x;
            if (x > right) right = x;
          }
        }
        if (count >= 2) { if (top < 0) top = y; bottom = y; }
      }
      if (top < 0 || bottom - top < h * 0.2) return;
      var boxH = (bottom + 1 - top) / h;
      var boxW = (right + 1 - left) / w;
      // A single bottle is tall and narrow; skip anything wider (cases, hampers, groups of bottles)
      if ((boxW * img.naturalWidth) / (boxH * img.naturalHeight) > 0.45) return;
      img.style.setProperty('--hh-bt', (top / h).toFixed(4));
      img.style.setProperty('--hh-bb', ((bottom + 1) / h).toFixed(4));
      img.style.setProperty('--hh-bx', (((left + right + 1) / 2) / w).toFixed(4));
      img.style.setProperty('--hh-ar', (img.naturalWidth / img.naturalHeight).toFixed(4));
      img.classList.add('is-bottle-aligned');
    } catch (error) {}
  }

  function initBottleAlign(scope) {
    (scope || document).querySelectorAll('.product-item__primary-image, .product-item__secondary-image, .hh-card__img').forEach(function (img) {
      if (img.complete && img.naturalWidth) measureBottle(img);
      else img.addEventListener('load', function () { measureBottle(img); }, { once: true });
    });
  }

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */
  function initScope(scope) {
    initSplit(scope);
    initReveal(scope);
    initParallax(scope);
    initCounters(scope);
    initRails(scope);
    initVideos(scope);
    initBottleAlign(scope);
  }

  function boot() {
    initAgeGate();
    initHeader();
    initDropdowns();
    initStateObservers();
    initCartCount();
    initCartRecap();
    initMemberTracking();
    initMemberLinks();
    initShipNudge();
    // Cards added later (sorting, filtering, recommendations, recently viewed): align them too
    if ('MutationObserver' in window) {
      var alignQueued = false;
      new MutationObserver(function () {
        if (alignQueued) return;
        alignQueued = true;
        requestAnimationFrame(function () { alignQueued = false; initBottleAlign(document); });
      }).observe(document.body, { childList: true, subtree: true });
    }
    initScope(document);
    root.classList.add('hh-ready');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor support
  document.addEventListener('shopify:section:load', function (event) {
    initScope(event.target);
    if (event.target.querySelector('[data-hh-header]')) {
      initHeader();
      initDropdowns();
      initStateObservers();
      initCartCount();
    }
  });
})();
